// The clock the appointment modal shows: a stored "HH:mm" as the office
// reads it, and the end of an appointment worked out from its start and
// length. One formatter for both, so the END TIME label cannot say
// "14:45 PM" beside a start field that says "2:15 PM".

/** "14:15" -> "2:15 PM". Anything that is not a time returns ''. */
export function formatClock(time: string): string {
  const [hours, mins] = String(time || '').split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return '';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(mins).padStart(2, '0')} ${ampm}`;
}

/** When an appointment starting at "HH:mm" and lasting this long ends. */
export function endTimeLabel(time: string, durationMinutes: number): string {
  const [hours, mins] = String(time || '').split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return '';
  const end = (hours * 60 + mins + (Number(durationMinutes) || 0)) % (24 * 60);
  return formatClock(`${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`);
}

/**
 * How long an appointment is when the editor opens it. Older and imported
 * appointments carry no length, only a start and an end; they opened as 30
 * minutes whatever they really were, and saving any other change cut a
 * 90-minute visit to a third. The stored length wins; failing that, the gap
 * between start and end; 30 only when there is nothing to work it out from.
 */
export function editorDuration(appointment: {
  duration?: number | null;
  startTime?: string | Date | null;
  endTime?: string | Date | null;
}): number {
  const stored = Number(appointment?.duration);
  if (stored > 0) return stored;
  const start = new Date(appointment?.startTime as string).getTime();
  const end = new Date(appointment?.endTime as string).getTime();
  const gap = Math.round((end - start) / 60000);
  return Number.isFinite(gap) && gap > 0 ? gap : 30;
}

const USUAL_DURATIONS = [15, 20, 30, 45, 60, 90, 120];

/** The lengths the editor offers — the usual ones, plus this appointment's own if it is not among them. */
export function durationChoices(current: number): number[] {
  const own = Number(current);
  if (!(own > 0) || USUAL_DURATIONS.includes(own)) return USUAL_DURATIONS;
  return [...USUAL_DURATIONS, own].sort((a, b) => a - b);
}

/** "9:30 AM" / "9:30am" / "9:30AM" -> "HH:mm". Incomplete text returns null. */
export function parseTypedTime(raw: string): string | null {
  const s = String(raw || '').trim().toUpperCase().replace(/\s+/g, ' ');
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 1 || h > 12 || min > 59) return null;
  if (m[3] === 'AM') h = h === 12 ? 0 : h;
  else h = h === 12 ? 12 : h + 12;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

// A time picked from three lists rather than typed: the hours on a clock
// face, minutes on the quarter hour, AM or PM. Nothing to misspell. Quarters,
// not fives: it is how an office schedules, and an odd start time breaks a
// provider's day into pieces nobody can book.
export const HOUR_CHOICES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
export const MINUTE_CHOICES = ['00', '15', '30', '45'];

/** (9, '15', 'AM') -> "09:15"; noon is 12 PM, midnight 12 AM. */
export function clockFromParts(hour: number, minute: string, period: 'AM' | 'PM'): string {
  const h = Number(hour) % 12 + (period === 'PM' ? 12 : 0);
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
